import {
  BaseEvent,
  HistoryMessageItem,
  useIM,
  MessageStatusType,
  MessageIncomingEvent,
  NotificationIncomingEvent,
  MessageCallReceived,
  isCallMessage,
} from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, ComputedRef, getCurrentScope, nextTick, onScopeDispose, ref, Ref } from 'vue'

import { useMarkUnread } from '@/features/mark-message-as-unread'

import { useCallsStore } from '@/entities/calls'
import { chatModel } from '@/entities/messages'
import { useNotificationItemStore } from '@/entities/notification'
import { useReactionsStore } from '@/entities/reaction'
import { useStoreLastReadEventIds } from '@/entities/room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { getDayJSInTimezone } from '@/shared/composables/timezone'
import { TIME_FORMAT } from '@/shared/utils/constants'
import { isEmojiWithoutTextCheck } from '@/shared/utils/emoji'

import { isSameMessageSeries } from './is-same-message-series'

interface UseChatEventResult {
  isEmoji: ComputedRef<boolean>;
  hhmm: ComputedRef<string>;
  isGroup: ComputedRef<boolean>;
  messageClasses: ComputedRef<string[]>;
  el: Ref<HTMLElement | undefined>;
  observer: Pick<IntersectionObserver, 'observe' | 'unobserve' | 'disconnect'>;
  lastReadEventId: ComputedRef<number | undefined>
}

const READ_VISIBILITY_RATIO = 0.6

const deletingUnMarkAsReadPServeds = new Set()

/** Сообщение выше области скролла никогда не набирает 60% intersectionRatio — достаточно любой видимой части. */
const isMessageVisibleEnoughForRead = (entry: IntersectionObserverEntry | undefined): boolean => {
  if (!entry?.isIntersecting) return false

  const viewportHeight = entry.rootBounds?.height ?? window.innerHeight
  const targetHeight = entry.boundingClientRect.height

  if (targetHeight > viewportHeight) {
    return entry.intersectionRatio > 0
  }

  return entry.intersectionRatio >= READ_VISIBILITY_RATIO
}

export function useChatEvent<T extends BaseEvent = BaseEvent> (
  event: Ref<HistoryMessageItem<T>>,
  next: Ref<HistoryMessageItem<T> | undefined>,
  onVisible?: () => void,
  needToCheckUnreadMessagesBeforeCall = false,
  isReadingLocked = ref(false),
): UseChatEventResult {
  const el = ref<HTMLElement>()

  const { markAsRead } = useIM()
  const { currentUser } = useCurrentUser()
  const { currentGuest } = useGuestAuth()
  const { updateEventStatus } = useCallsStore()
  const { markReactionNotificationAsRead } = useNotificationItemStore()
  const { reactionEvents } = storeToRefs(useReactionsStore())

  const {
    getMessageEventIdByRoomAndContactId,
    updateLastReadEventId,
    getOwnMessageRankByRoomAndContactId,
    getHasUnreadMessageBeforeCall,
    getMarkedUnread,
  } = useStoreLastReadEventIds()

  const pServed = computed(() => {
    return event.value.item.pServed
  })

  const currentUserImLogin = currentUser.value?.imLogin ?? currentGuest.value?.pServed

  if (!currentUserImLogin) {
    throw new Error('Can\'t use chat event without IM login')
  }

  const isGroup = computed(() => isSameMessageSeries(event.value, next.value))

  const hhmm = computed(() => {
    if (!event.value.timestamp) return ''

    return getDayJSInTimezone(event.value.timestamp).format(TIME_FORMAT)
  })

  const messageClasses = computed(() => {
    const classes: string[] = []
    if (isEmoji.value) {
      classes.push('text-emoji-r-80-80')
    } else {
      classes.push('text-body-r-15-22')
    }

    return classes
  })

  const isEmoji = computed(() => {
    if (event.value.item.type !== 'IncomingMessageEvent' && event.value.item.type !== 'OutgoingMessageEvent') {
      return false
    }
    return isEmojiWithoutTextCheck((event.value.item as unknown as MessageIncomingEvent).messageText)
  })

  let isDisposed = false
  let intersectionObserver: IntersectionObserver | undefined

  const ensureObserver = (): IntersectionObserver => {
    if (!intersectionObserver) {
      intersectionObserver = new IntersectionObserver(async (e) => {
        await nextTick()
        if (isDisposed) {
          return
        }
        const entry = e[0]
        if (!isMessageVisibleEnoughForRead(entry)) {
          return
        }
        if (onVisible) {
          onVisible()
        }
        if (!(
          isCallMessage(event.value) &&
          needToCheckUnreadMessagesBeforeCall &&
          getHasUnreadMessageBeforeCall(event.value as unknown as HistoryMessageItem<MessageCallReceived>)
        )) {
          await read()
        }
      }, { threshold: [0, READ_VISIBILITY_RATIO] })
    }
    return intersectionObserver
  }

  const observer: Pick<IntersectionObserver, 'observe' | 'unobserve' | 'disconnect'> = {
    observe: (target) => ensureObserver().observe(target),
    unobserve: (target) => intersectionObserver?.unobserve(target),
    disconnect: () => intersectionObserver?.disconnect(),
  }

  const read = async () => {
    if (isDisposed || !pServed.value || isReadingLocked.value) return

    const markedUnread = getMarkedUnread(pServed.value)
    if (markedUnread && markedUnread.rank <= event.value.rank && !deletingUnMarkAsReadPServeds.has(pServed.value)) {

      deletingUnMarkAsReadPServeds.add(pServed.value)
      await useMarkUnread(pServed.value).onDeleteMarkAsUnread(markedUnread.eventId)
      deletingUnMarkAsReadPServeds.delete(pServed.value)
      if (isDisposed) return
    }

    const lastReadMessageRank = getOwnMessageRankByRoomAndContactId(pServed.value)
    const { type } = event.value.item
    const alreadyRead = lastReadMessageRank >= event.value.rank || type === 'OutgoingMessageEvent'
    const { eventId } = event.value

    // Отмечаем уведомления о реакциях как прочитанные при видимости сообщения с реакцией
    const messageReactions = reactionEvents.value.get(eventId)
    if (messageReactions && messageReactions.size > 0) {
      markReactionNotificationAsRead(eventId, pServed.value)
    }

    if (alreadyRead) return

    const result = await markAsRead({
      eventIdMarkedAsRead: eventId,
      to: pServed.value,
    })
    if (isDisposed || !result) {
      return
    }
    chatModel.useChatMessageStore().updateMessageStatus(
      eventId,
      pServed.value,
      currentUserImLogin,
      MessageStatusType.READ,
    )
    updateLastReadEventId(pServed.value, currentUserImLogin, eventId, event.value.rank)
    if (el.value) {
      observer.unobserve(el.value)
    }

    if (type === 'MessageCallReceived') {
      updateEventStatus(eventId, MessageStatusType.READ)
    }

    const notificationEventId = (event.value.item as unknown as NotificationIncomingEvent).notificationEventId
    if (notificationEventId) {
      await markAsRead({
        eventIdMarkedAsRead: notificationEventId,
        to: pServed.value,
      })
    }
  }

  const lastReadEventId = computed(() => {
    if (!currentUser.value || !pServed.value) {
      return
    }
    return getMessageEventIdByRoomAndContactId(pServed.value, currentUser.value?.imLogin)
  })

  if (getCurrentScope()) {
    onScopeDispose(() => {
      isDisposed = true
      observer.disconnect()
      el.value = undefined
    })
  }

  return {
    isEmoji,
    hhmm,
    isGroup,
    el,
    messageClasses,
    observer,
    lastReadEventId,
  }
}
